import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, FileSpreadsheet, XCircle } from 'lucide-react';
import { FileDropzone } from '../../onboarding/FileDropzone';
import { ApiError } from '../../../lib/api';
import {
  confirmInvoiceImport,
  previewInvoiceImport,
  type InvoiceImportPreview,
  type InvoiceImportResult,
  type InvoiceImportRow,
} from '../../../lib/onboardingImportApi';
import { useCompanies } from '../../../hooks/useCompanies';
import { useActiveCompanyId } from '../../../store/useCompanyStore';
import { Select } from '../../ui/Select';
import { ProjectMappingPanel, suggestedProjectMap } from '../../imports/ProjectMappingPanel';

// Facturi → „Importă din Excel”: the invoice history (04_facturi.xlsx).
//   1. Upload  — firm + file.
//   2. Preview — what happens to every row (new / status update / unchanged /
//      skipped / error), the project codes to create or map, then confirm.
//   3. Result  — counts + per-row failures.
// The confirm sends the same file again; the server refuses it if the result
// no longer matches this preview (someone imported or edited in between).

const MAX_BYTES = 10 * 1024 * 1024;

type Step = 'upload' | 'preview' | 'result';
type Filter = 'all' | 'problems' | 'create' | 'update';

const ACTION_LABEL: Record<InvoiceImportRow['action'], string> = {
  create: 'Nouă',
  update: 'Actualizare status',
  unchanged: 'Există deja',
  skip: 'Omisă',
};

const STATUS_LABEL: Record<InvoiceImportRow['data']['invoiceStatus'], string> = {
  DRAFT: 'Ciornă',
  SENT: 'Deschisă',
  OVERDUE: 'Restantă',
  PAID: 'Plătită',
  CANCELLED: 'Anulată',
};

export function InvoiceImportDialog({
  onClose,
  onImportComplete,
  companyId: fixedCompanyId,
}: {
  onClose: () => void;
  onImportComplete: (result?: InvoiceImportResult) => void;
  /** Import into this firm (Setări → Onboarding picks it); the picker starts on it. */
  companyId?: string | null;
}) {
  const qc = useQueryClient();
  const { data: companies } = useCompanies();
  const activeCompanyId = useActiveCompanyId();
  const [pickedCompany, setPickedCompany] = useState<string | null>(fixedCompanyId ?? null);
  const companyId =
    pickedCompany ??
    activeCompanyId ??
    companies?.find((c) => c.isDefault)?.id ??
    companies?.[0]?.id ??
    null;
  const companyLabel = (() => {
    const c = companies?.find((x) => x.id === companyId);
    return c ? c.displayName || c.legalName : null;
  })();

  const [step, setStep] = useState<Step>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<InvoiceImportPreview | null>(null);
  const [projectMap, setProjectMap] = useState<Record<string, string>>({});
  const [result, setResult] = useState<InvoiceImportResult | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [busy, setBusy] = useState<'reading' | 'importing' | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function runPreview(f: File, map: Record<string, string>) {
    setBusy('reading');
    setError(null);
    try {
      const first = await previewInvoiceImport(f, { companyId, projectMap: map });
      // First read: propose the server's suggestions, visibly, in the panel.
      const suggested = Object.keys(map).length === 0 ? suggestedProjectMap(first.projects) : {};
      if (Object.keys(suggested).length > 0) {
        setProjectMap(suggested);
        setPreview(await previewInvoiceImport(f, { companyId, projectMap: suggested }));
      } else {
        setPreview(first);
      }
      setStep('preview');
    } catch (err) {
      setError(messageOf(err, 'Fișierul nu a putut fi citit.'));
    } finally {
      setBusy(null);
    }
  }

  function handleFile(f: File) {
    setError(null);
    if (!/\.xlsx$/i.test(f.name)) {
      setError('Este acceptat doar un fișier .xlsx.');
      return;
    }
    if (f.size > MAX_BYTES) {
      setError('Fișierul depășește limita de 10MB.');
      return;
    }
    setFile(f);
    setProjectMap({});
    void runPreview(f, {});
  }

  function changeProjectMap(next: Record<string, string>) {
    setProjectMap(next);
    if (file) void runPreview(file, next);
  }

  async function handleImport() {
    if (!file || !preview) return;
    setBusy('importing');
    setError(null);
    try {
      const res = await confirmInvoiceImport(file, {
        companyId,
        previewHash: preview.previewHash,
        projectMap,
      });
      setResult(res);
      setStep('result');
      void qc.invalidateQueries({ queryKey: ['invoices'] });
      void qc.invalidateQueries({ queryKey: ['projects'] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
      onImportComplete(res);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'PREVIEW_CHANGED') {
        setError(err.message);
        void runPreview(file, projectMap);
      } else {
        setError(messageOf(err, 'Importul a eșuat.'));
      }
    } finally {
      setBusy(null);
    }
  }

  const rows = useMemo(() => {
    const all = preview?.rows ?? [];
    switch (filter) {
      case 'problems':
        return all.filter((r) => r.status !== 'valid');
      case 'create':
        return all.filter((r) => r.action === 'create' && r.status !== 'error');
      case 'update':
        return all.filter((r) => r.action === 'update');
      default:
        return all;
    }
  }, [preview, filter]);

  const s = preview?.summary;
  const blocking = (s?.errors ?? 0) > 0;
  const applicable = (s?.create ?? 0) + (s?.update ?? 0);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-brand-600" />
            <h2 className="text-lg font-semibold text-gray-900">Importă facturi din Excel</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy === 'importing'}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Închide"
          >
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {error && (
            <div className="mb-4 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              <XCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {step === 'upload' && (
            <div className="space-y-4">
              {companies && companies.length > 0 && (
                <Select
                  label="Firma ale cărei facturi le imporți"
                  value={companyId ?? ''}
                  onChange={(v) => setPickedCompany(v)}
                  options={companies.map((c) => ({
                    value: c.id,
                    label: c.displayName || c.legalName,
                  }))}
                />
              )}
              <p className="text-sm text-gray-600">
                Fișierul <span className="font-mono">04_facturi.xlsx</span> din folderul de
                onboarding: foaia FACTURI (o factură pe rând) și, opțional, foaia PROIECTE.
                Facturile care sunt deja în Firmio (din SPV, emise din Firmio sau dintr-un import
                anterior) nu se dublează: li se actualizează doar statusul.
              </p>
              <FileDropzone
                accept=".xlsx"
                hint="Un fișier .xlsx, până la 10MB"
                disabled={busy !== null}
                onFile={handleFile}
              />
              {busy === 'reading' && (
                <Spinner text="Se citește fișierul și se compară cu Firmio…" />
              )}
            </div>
          )}

          {step === 'preview' && preview && s && (
            <>
              <p className="mb-3 text-sm text-gray-600">
                Firma: <span className="font-medium text-gray-900">{companyLabel}</span> · fișier{' '}
                <span className="font-medium text-gray-900">{file?.name}</span>
              </p>
              <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-5">
                <Stat label="Noi" value={s.create} tone="green" />
                <Stat label="Actualizare status" value={s.update} tone="blue" />
                <Stat label="Există deja" value={s.unchanged} tone="gray" />
                <Stat label="Omise" value={s.skip} tone="amber" />
                <Stat label="Erori" value={s.errors} tone="red" />
              </div>

              <ProjectMappingPanel
                projects={preview.projects}
                projectMap={projectMap}
                onChange={changeProjectMap}
                disabled={busy !== null}
              />

              {blocking && (
                <p className="mb-2 flex items-center gap-2 text-xs text-red-600">
                  <XCircle className="h-3.5 w-3.5" />
                  Rândurile cu erori nu se importă. Restul se pot importa acum, iar după ce
                  corectezi fișierul, un import nou adaugă doar ce lipsește.
                </p>
              )}

              <div className="mb-2 flex flex-wrap gap-1 text-xs">
                {(
                  [
                    ['all', `Toate (${s.total})`],
                    ['problems', `Cu observații (${s.warnings + s.errors})`],
                    ['create', `Noi (${s.create})`],
                    ['update', `Actualizări (${s.update})`],
                  ] as [Filter, string][]
                ).map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setFilter(key)}
                    className={`rounded border px-2 py-1 ${
                      filter === key
                        ? 'border-brand-500 bg-brand-50 text-brand-700'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <div className="max-h-[420px] overflow-auto rounded-md border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200 text-xs">
                  <thead className="sticky top-0 z-10 bg-gray-50 text-left font-semibold uppercase tracking-wide text-gray-600">
                    <tr>
                      <th className="px-2 py-2">Rând</th>
                      <th className="px-2 py-2" />
                      <th className="px-2 py-2">Factură</th>
                      <th className="px-2 py-2">Partener</th>
                      <th className="px-2 py-2 text-right">Total</th>
                      <th className="px-2 py-2">Status</th>
                      <th className="px-2 py-2">Proiect</th>
                      <th className="px-2 py-2">Ce se întâmplă</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {rows.map((r) => (
                      <tr key={r.rowNumber} className={r.status === 'error' ? 'bg-red-500/10' : ''}>
                        <td className="px-2 py-1.5 align-top text-gray-500">{r.rowNumber}</td>
                        <td className="px-2 py-1.5 align-top">
                          <StatusIcon status={r.status} />
                        </td>
                        <td className="px-2 py-1.5 align-top">
                          <span className="font-medium text-gray-900">{r.data.invoiceNumber}</span>
                          <span className="block text-gray-500">
                            {r.data.direction === 'INCOME' ? 'emisă' : 'primită'} ·{' '}
                            {r.data.issueDate}
                          </span>
                        </td>
                        <td className="px-2 py-1.5 align-top text-gray-700">
                          {r.data.partnerName || '—'}
                        </td>
                        <td className="whitespace-nowrap px-2 py-1.5 text-right align-top text-gray-900">
                          {r.data.total.toLocaleString('ro-RO', { minimumFractionDigits: 2 })}{' '}
                          {r.data.currency}
                        </td>
                        <td className="px-2 py-1.5 align-top text-gray-700">
                          {STATUS_LABEL[r.data.invoiceStatus]}
                          {r.data.paidAt && (
                            <span className="block text-gray-500">{r.data.paidAt}</span>
                          )}
                        </td>
                        <td className="px-2 py-1.5 align-top font-mono text-gray-600">
                          {r.data.projectCode ?? '—'}
                        </td>
                        <td className="px-2 py-1.5 align-top">
                          <span className="text-gray-800">
                            {r.status === 'error' ? 'Eroare' : ACTION_LABEL[r.action]}
                          </span>
                          {r.issues.map((issue, i) => (
                            <span
                              key={i}
                              className={`block ${r.status === 'error' ? 'text-red-700' : 'text-amber-700'}`}
                            >
                              {issue}
                            </span>
                          ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {step === 'result' && result && (
            <div className="space-y-4">
              <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 p-4">
                <p className="flex items-center gap-2 text-sm font-semibold text-emerald-700">
                  <CheckCircle2 className="h-4 w-4" />
                  Import finalizat
                </p>
                <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-5">
                  <Stat label="Facturi create" value={result.created} tone="green" />
                  <Stat label="Statusuri actualizate" value={result.updated} tone="blue" />
                  <Stat label="Neschimbate" value={result.unchanged} tone="gray" />
                  <Stat label="Proiecte create" value={result.projectsCreated} tone="green" />
                  <Stat label="Eșuate" value={result.failed.length} tone="red" />
                </div>
              </div>
              {result.failed.length > 0 && (
                <ul className="space-y-1 text-sm text-red-700">
                  {result.failed.map((f) => (
                    <li key={f.rowNumber}>
                      Rândul {f.rowNumber}: {f.error}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-5 py-3">
          {step === 'preview' && (
            <>
              {busy === 'reading' && <Spinner text="Se recalculează…" />}
              <button
                type="button"
                onClick={() => {
                  setStep('upload');
                  setPreview(null);
                  setFile(null);
                }}
                disabled={busy !== null}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Alege alt fișier
              </button>
              <button
                type="button"
                onClick={() => void handleImport()}
                disabled={busy !== null || applicable === 0}
                className="inline-flex items-center gap-2 rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
              >
                {busy === 'importing'
                  ? 'Se importă…'
                  : `Importă (${s?.create ?? 0} noi, ${s?.update ?? 0} actualizări)`}
              </button>
            </>
          )}
          {step !== 'preview' && (
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              {step === 'result' ? 'Gata' : 'Anulează'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Spinner({ text }: { text: string }) {
  return (
    <p className="flex items-center gap-2 text-sm text-gray-500">
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
      {text}
    </p>
  );
}

function StatusIcon({ status }: { status: InvoiceImportRow['status'] }) {
  if (status === 'valid')
    return <CheckCircle2 className="h-4 w-4 text-green-600" aria-label="Valid" />;
  if (status === 'warning')
    return <AlertTriangle className="h-4 w-4 text-amber-500" aria-label="Avertisment" />;
  return <XCircle className="h-4 w-4 text-red-600" aria-label="Eroare" />;
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'green' | 'blue' | 'amber' | 'red' | 'gray';
}) {
  const toneClass = {
    green: 'text-green-700',
    blue: 'text-blue-700',
    amber: 'text-amber-700',
    red: 'text-red-700',
    gray: 'text-gray-700',
  }[tone];
  return (
    <div className="rounded border border-gray-200 bg-white px-3 py-2 text-center">
      <p className="text-xs text-gray-500">{label}</p>
      <p className={`text-lg font-semibold ${toneClass}`}>{value}</p>
    </div>
  );
}

function messageOf(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return fallback;
}
